/**
 * Epic Features Kanban
 *
 * Displays a Kanban board filtered to show only features belonging to a specific epic.
 * Reuses the BugReportKanbanView component with filtered data.
 */

import { useMemo } from 'react';
import { BugReport } from 'shared/types/bug-report';
import { BugReportKanbanView } from 'screens/bug-reports/components/BugReportKanbanView';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlusIcon } from '@phosphor-icons/react';

interface EpicFeaturesKanbanProps {
  features: BugReport[];
  epicId: number;
  onUpdate: () => void;
  onFeatureClick?: (feature: BugReport) => void;
  onAddFeature?: () => void;
}

export const EpicFeaturesKanban: React.FC<EpicFeaturesKanbanProps> = ({
  features,
  epicId,
  onUpdate,
  onFeatureClick,
  onAddFeature
}) => {
  // Filter features to only those belonging to this epic
  const epicFeatures = useMemo(() => {
    return features.filter((f) => f.epicId === epicId);
  }, [features, epicId]);

  if (epicFeatures.length === 0) {
    return (
      <Card className="p-12 text-center">
        <div className="space-y-4">
          <div className="text-neutral-500">
            <p className="text-lg font-medium">Geen features in deze epic</p>
            <p className="text-sm mt-2">
              Voeg features toe om ze te kunnen beheren en volgen
            </p>
          </div>
          {onAddFeature && (
            <Button onClick={onAddFeature}>
              <PlusIcon size={20} className="mr-2" />
              Feature Toevoegen
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          Features ({epicFeatures.length})
        </h2>
        {onAddFeature && (
          <Button size="sm" onClick={onAddFeature}>
            <PlusIcon size={16} className="mr-2" />
            Feature Toevoegen
          </Button>
        )}
      </div>

      {/* Kanban Board */}
      <BugReportKanbanView
        bugs={epicFeatures}
        onUpdate={onUpdate}
        onBugClick={onFeatureClick}
      />
    </div>
  );
};
